import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'

import { Field } from '@/components/ui/Field'
import { cn } from '@/lib/utils/cn'

import styles from './TextField.module.css'

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string
  error?: string
  hint?: string
  optional?: boolean
  size?: 'md' | 'lg'
  leadingIcon?: ReactNode
  trailing?: ReactNode
  className?: string
  inputClassName?: string
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    label,
    error,
    hint,
    optional,
    size = 'md',
    leadingIcon,
    trailing,
    id,
    className,
    inputClassName,
    ...rest
  },
  ref,
) {
  return (
    <Field
      label={label}
      error={error}
      hint={hint}
      optional={optional}
      id={id}
      className={className}
    >
      {(control) => (
        <div
          className={cn(
            styles.control,
            styles[size],
            leadingIcon && styles.hasLeading,
            trailing && styles.hasTrailing,
          )}
        >
          {leadingIcon && <span className={styles.leading}>{leadingIcon}</span>}

          <input
            {...rest}
            {...control}
            ref={ref}
            className={cn(styles.input, error && styles.invalid, inputClassName)}
          />

          {trailing && <span className={styles.trailing}>{trailing}</span>}
        </div>
      )}
    </Field>
  )
})
